import React, { ReactNode } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  EventTimelineItemFormData,
  EventTimelineItemFields,
} from '~admin/view/components/details/event-details/event-timeline-details/event-timeline-item-fields.component';
import { getEventTimelineItemInitialValues } from '~admin/view/components/create-event/helper/create-event-initial-values.helper';
import { TextField } from '~common/components/form/field-text.component';
import {
  TranslationField,
  TranslationFormData,
} from '~admin/view/components/ui/form/fields/field-translation.component';
import { Box } from '~common/components/box.component';
import { Button } from '~common/components/button/button.component';
import { Size } from '~common/components/button/button.helper';
import { Column } from '~common/components/column.component';
import { Color } from '~common/model/basics';
import { DeleteIcon } from '~web/view/components/ui/icon/delete.icon';
import { PlusIcon } from '~web/view/components/ui/icon/plus.icon';

interface Props {
  name: string;
  onRemove?: VoidFunction;
}

export interface EventTimelineGroupFormData {
  title?: TranslationFormData<string>[];
  eventTimelineItems: EventTimelineItemFormData[];
}

export const EventTimelineGroup: React.FunctionComponent<Props> = ({ name, onRemove }) => {
  const { t } = useTranslation();

  return (
    <Container>
      <Top>
        <RestrictBox>
          <TranslationField
            name={`${name}.title`}
            label={t('createEvent.eventDetails.timeline.timelineGroup.title')}
            render={(renderProps): ReactNode => <TextField {...renderProps} />}
          />
        </RestrictBox>
        <DeleteButton
          color={Color.White}
          size={Size.Small}
          onClick={(): void => {
            onRemove?.();
          }}
        >
          <DeleteIcon color={Color.Black} />
        </DeleteButton>
      </Top>
      <FieldArray name={`${name}.eventTimelineItems`}>
        {({ fields }): ReactNode => {
          const onRemove = (index: number): void => {
            fields.remove(index);
          };

          const onAdd = (): void => {
            fields.push(getEventTimelineItemInitialValues(fields.length + 1));
          };

          return (
            <>
              {fields.map((name: string, index: number) => (
                <EventTimelineItemFields key={name} name={name} index={index} onRemove={onRemove} />
              ))}
              <Box>
                <Button color={Color.White} size={Size.Small} onClick={onAdd}>
                  <StyledPlusIcon color={Color.Black} width={14} height={14} />
                  {t('createEvent.eventDetails.timeline.timelineGroup.addButton')}
                </Button>
              </Box>
            </>
          );
        }}
      </FieldArray>
    </Container>
  );
};

const Container = styled(Column)`
  flex: 1 1 auto;
  border: 2px solid ${(props): string => props.theme.colors.gray.v3};
  border-radius: 16px;
  padding: 16px;

  > div:not(:first-child) {
    margin-top: 16px;
  }
`;

const StyledPlusIcon = styled(PlusIcon)`
  fill: currentColor;
  margin-right: 12px;
`;

const Top = styled(Box)`
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const RestrictBox = styled(Box)`
  flex: 1 1 auto;
  margin-right: 32px;
`;

const DeleteButton = styled(Button)`
  && {
    min-width: 44px;
    width: 44px;
    height: 44px;
    padding: 0;
  }
`;



// WEBPACK FOOTER //
// ./view/components/details/event-details/event-timeline-details/event-timeline-group.component.tsx